import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, MapPin, Phone, Send } from 'lucide-react';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message sent! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setLoading(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'hello@portlandtech.org',
      href: 'mailto:hello@portlandtech.org',
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Portland, Oregon',
      href: null,
    },
  ];

  const partnershipTypes = [
    'Event Sponsorship',
    'Community Partnerships',
    'Content Collaboration',
    'Job Board Listings',
    'Directory Promotion',
  ];

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Connect</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Contact & Partnerships</h1>
          <p className="text-muted-foreground max-w-2xl">
            Get in touch with us. Whether you have questions, want to collaborate, 
            or are interested in partnership opportunities.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="text-heading text-2xl mb-6">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Name</label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Email</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Subject</label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Message</label>
                  <Textarea
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    data-testid="contact-message"
                  />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit">
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-heading text-2xl mb-6">Get in Touch</h2>
              <div className="space-y-6 mb-12">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{item.title}</div>
                      {item.href ? (
                        <a href={item.href} className="font-medium hover:text-primary transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <div className="font-medium">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Partnership Types */}
              <h3 className="font-semibold mb-4">Partnership Opportunities</h3>
              <ul className="space-y-2">
                {partnershipTypes.map((type, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="w-1 h-1 bg-muted-foreground rounded-full" />
                    {type}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30">
        <div className="container-wide py-16">
          <h2 className="text-heading text-2xl mb-8">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">How do I add my startup to the directory?</h3>
                <p className="text-sm text-muted-foreground">
                  Contact us with your company details, and our admin team will review 
                  and add your listing to the directory.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">How can I promote my event?</h3>
                <p className="text-sm text-muted-foreground">
                  Submit your event details through our contact form. We feature 
                  relevant tech events in our calendar and newsletter.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Do you offer sponsorship packages?</h3>
                <p className="text-sm text-muted-foreground">
                  Yes! We offer various sponsorship opportunities for events, 
                  newsletters, and directory features. Reach out to discuss options.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">How can I post a job listing?</h3>
                <p className="text-sm text-muted-foreground">
                  Admin users can add job listings directly. Contact us to get 
                  your company set up with posting access.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
